import React, { useEffect, useMemo, useState } from 'react';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { GlassButton } from 'dashboard/components/common';
import useBreakpoint from 'hooks/useBreakpoint';
import PageCanvas from './PageCanvas';
import SectionInspector from './SectionInspector';

/**
 * The page builder as a page, not a form.
 *
 * Canvas on the left, settings for whatever is selected on the right — and on
 * a phone, the settings come up as a sheet over the canvas instead, because
 * there is no room for two columns and a sheet is what a phone does.
 *
 * The old editor was a stack of collapsible cards: every section's settings
 * visible at once, each a set of dropdowns with no relationship to where the
 * thing sat on the page. It was complete and unreadable. This shows the page
 * and lets you point at what you want to change.
 *
 * The list editor is still there behind a toggle. It is better for bulk work
 * — reordering eight sections, checking every background colour at once — and
 * throwing it away to make a point about the new one would cost people a
 * workflow they already have.
 */
export default function VisualEditor({
  page, brand, catalog, brandId,
  onPatchSection, onRemoveSection, onReorderRegion,
  onComponentChange, onAddSectionAt, focusSectionId, onFocused,
  toolbar = null,
}) {
  const { isMobile } = useBreakpoint();
  const [selectedId, setSelectedId] = useState(null);

  // Memoised because the effect below depends on it: a fresh [] every render
  // would re-run the effect on every render.
  const sections = useMemo(() => page?.sectionData || [], [page]);
  const selected = sections.find((s) => s._id === selectedId) || null;

  // A section deleted while selected leaves the inspector pointed at nothing.
  useEffect(() => {
    if (selectedId && !sections.some((s) => s._id === selectedId)) setSelectedId(null);
  }, [sections, selectedId]);

  // A section that was just added opens straight into the inspector. It
  // arrives hidden and empty, so leaving someone to find the new faint band on
  // the canvas and click it is asking them to hunt for their own click.
  useEffect(() => {
    if (!focusSectionId) return;
    if (!sections.some((s) => s._id === focusSectionId)) return;
    setSelectedId(focusSectionId);
    onFocused?.();
  }, [focusSectionId, sections, onFocused]);

  const moveSection = (section, delta) => {
    // Reordering happens within a region — a footer section cannot become the
    // second thing on the page — so the neighbour is found among its own kind.
    const siblings = sections
      .filter((s) => s.region === section.region)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const i = siblings.findIndex((s) => s._id === section._id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= siblings.length) return;
    const ids = siblings.map((s) => s._id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    onReorderRegion(section.region, ids);
  };

  const inspector = (
    <SectionInspector
      section={selected}
      page={page}
      catalog={catalog}
      brandId={brandId}
      onPatch={(patch) => onPatchSection(selected._id, patch)}
      onComponentChange={(action, payload) =>
        onComponentChange(action, { section_id: selected._id, ...payload })}
      onAddComponent={(sec, type, parent_id) =>
        onComponentChange('add', { section_id: sec._id, type, parent_id })}
    />
  );

  return (
    <Box>
      {/**
       * One control row, above both columns.
       *
       * It used to be two: the view switcher in a row of its own, then this
       * one. Two rows holding three controls between them left a wide empty
       * band down the left of the editor, and because this row sat INSIDE the
       * canvas column, the settings panel beside it started a row higher than
       * the page it belongs to — the two columns never lined up at the top.
       *
       * `toolbar` is how the view switcher gets in here without this
       * component having to know what a view switcher is.
       */}
      <Stack
        direction='row'
        alignItems='center'
        gap='8px'
        flexWrap='wrap'
        mb='10px'
      >
        <Typography variant='caption' color='text.secondary'>
          Click any part of the page to edit it.
        </Typography>
        <Box sx={{ flex: 1 }} />
        <GlassButton size='small' startIcon={<AddIcon />} onClick={() => onAddSectionAt('main', null)}>
          Add section
        </GlassButton>
        {toolbar}
      </Stack>

    <Stack direction={{ xs: 'column', lg: 'row' }} spacing='16px' alignItems='flex-start'>
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <PageCanvas
          page={page}
          brand={brand}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onMoveSection={moveSection}
          onRemoveSection={(s) => onRemoveSection(s._id)}
          onToggleSection={(s) => onPatchSection(s._id, { isVisible: s.isVisible === false })}
          onAddBelow={(s) => onAddSectionAt(s.region, s)}
          onDropSection={(dragId, dropId) => {
            const from = sections.find((x) => x._id === dragId);
            if (!from) return;
            const siblings = sections
              .filter((x) => x.region === from.region)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((x) => x._id);
            const i = siblings.indexOf(dragId);
            const j = siblings.indexOf(dropId);
            if (i < 0 || j < 0) return;
            // Move to the target's position rather than swapping: dragging
            // section 1 onto section 4 should put it fourth, not trade places.
            siblings.splice(j, 0, siblings.splice(i, 1)[0]);
            onReorderRegion(from.region, siblings);
          }}
        />
      </Box>

      {/* Desktop: a column beside the page, so a change and its result are on
          screen together. */}
      {!isMobile && (
        <Box
          sx={{
            width: 360, flexShrink: 0,
            border: '1px solid', borderColor: 'divider', borderRadius: '12px',
            position: 'sticky', top: '88px',
            maxHeight: 'calc(100vh - 110px)', overflowY: 'auto',
          }}
        >
          {inspector}
        </Box>
      )}

      {/* Phone: a sheet over the page. It stops at 85% so a strip of the
          canvas stays visible — editing a colour while the thing you are
          colouring is hidden is guesswork. */}
      {isMobile && (
        <Drawer
          anchor='bottom'
          open={Boolean(selected)}
          onClose={() => setSelectedId(null)}
          PaperProps={{
            sx: {
              maxHeight: '85vh',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            },
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ p: '8px 8px 0 16px' }}
          >
            <Typography variant='body2' fontWeight='700'>
              {selected?.name || selected?.type}
            </Typography>
            <IconButton size='small' onClick={() => setSelectedId(null)} aria-label='Close'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Stack>
          {inspector}
        </Drawer>
      )}
    </Stack>
    </Box>
  );
}
